import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  APIProvider,
  Map,
  AdvancedMarker,
  ControlPosition,
  useMap,
} from "@vis.gl/react-google-maps";

import RoutePath from "components/Maps/ActiveRoutes/RoutePath";
import { useMapPathZoom } from "components/Maps/ActiveRoutes/use-map-zoom";
import { API_KEY } from "config/maps";
import { resolveRouteColorPalette } from "components/Maps/ActiveRoutes/routeColor";
import EndpointBothIcon from "resources/route-builder/endpoint-both.svg";
import EndpointEndIcon from "resources/route-builder/endpoint-end.svg";
import EndpointStartIcon from "resources/route-builder/endpoint-start.svg";
import UnassignedTriangleIcon from "resources/route-builder/unassigned-triangle.svg";
import type { RouteStop } from "types/routeBuilder";
import { toLatLngLiteral } from "utils/geo";
import { formatAddress } from "pages/Admin/Routes/RoutePage/Routes/utils";
import {
  UNASSIGNED_ROUTE_ID,
  useRouteBuilder,
} from "pages/Admin/Routes/RouteBuilder/context/RouteBuilderContext";
import { unassignedMapStops } from "pages/Admin/Routes/RouteBuilder/lib/unassignedMapStops";
import {
  mapKeyMarks,
  type MapKeyMark,
} from "pages/Admin/Routes/RouteBuilder/lib/mapKey";
import {
  PROPERTY_FOCUS_ANIMATION_MS,
  propertyFocusCenter,
  propertyFocusPanByX,
  propertyFocusSample,
  propertyFocusZoom,
} from "pages/Admin/Routes/RouteBuilder/lib/propertyFocusCamera";

const MAP_ID = "8cbff7fc95a543f2";
const EMPTY_PATHS: string[] = [];
// Fade routes other than the selected one so it stands out (issue #551).
const DIMMED_PATH_OPACITY = 0.4;
const STALE_PATH_OPACITY = 0.5;

function EndpointIcon({ kind }: { kind: "start" | "end" | "both" }) {
  const src =
    kind === "start"
      ? EndpointStartIcon
      : kind === "end"
        ? EndpointEndIcon
        : EndpointBothIcon;
  return (
    <img
      src={src}
      alt=""
      className={kind === "both" ? "size-4" : "h-4 w-4"}
      aria-hidden
    />
  );
}

function MapKey({ marks }: { marks: MapKeyMark[] }) {
  const labels: Record<MapKeyMark, string> = {
    both: "Location",
    routeStop: "Route stop",
    unassigned: "Unassigned",
    start: "Start",
    end: "End",
  };
  return (
    // Keep the right-hand zoom controls and bottom attribution clear, even when the key wraps.
    <aside
      aria-label="Map key"
      data-testid="route-builder-map-key"
      className="pointer-events-none absolute bottom-6 left-3 right-16 z-20"
    >
      <div className="w-fit max-w-full rounded-lg border border-slate-200 bg-white px-3 py-2 shadow-sm">
        <ul className="flex flex-wrap items-center gap-x-3 gap-y-1.5 text-xs text-slate-700">
          {marks.map((mark) => (
            <li key={mark} className="flex items-center gap-1.5">
              {mark === "routeStop" ? (
                <span
                  aria-hidden
                  className="flex h-5 w-5 items-center justify-center rounded-full bg-emerald-600 text-[10px] font-bold text-white"
                >
                  1
                </span>
              ) : mark === "unassigned" ? (
                <span
                  aria-hidden
                  className="relative flex h-5 w-5 items-center justify-center text-[10px] font-bold text-white"
                >
                  <img
                    src={UnassignedTriangleIcon}
                    alt=""
                    className="absolute inset-0 h-full w-full"
                  />
                  <span className="relative">1</span>
                </span>
              ) : (
                <span className="flex h-5 w-5 items-center justify-center rounded-full border-2 border-sky-500 bg-white">
                  <EndpointIcon kind={mark} />
                </span>
              )}
              {labels[mark]}
            </li>
          ))}
        </ul>
        {marks.length === 0 ? (
          <p className="text-xs text-slate-500">No markers visible</p>
        ) : null}
      </div>
    </aside>
  );
}

function drawerShiftedCenter(
  map: google.maps.Map,
  position: { lat: number; lng: number },
  panByX: number,
  zoom: number,
) {
  const projection = map.getProjection();
  if (!projection) return position;
  return propertyFocusCenter(
    position,
    panByX,
    zoom,
    (lat, lng) => {
      const point = projection.fromLatLngToPoint(
        new google.maps.LatLng(lat, lng),
      );
      return point ? { x: point.x, y: point.y } : null;
    },
    (x, y) => {
      const latLng = projection.fromPointToLatLng(new google.maps.Point(x, y));
      return latLng ? { lat: latLng.lat(), lng: latLng.lng() } : null;
    },
  );
}

function FocusPropertyOnMap({
  nonce,
  position,
}: {
  nonce: number;
  position: { lat: number; lng: number } | null;
}) {
  const map = useMap();
  // Skip a request that is already current when this map mounts so switching
  // back from List still frames the whole route.
  const seenNonce = useRef(nonce);
  const animationRef = useRef(0);

  const lat = position?.lat;
  const lng = position?.lng;

  useEffect(() => {
    if (!map || nonce === seenNonce.current) return;
    if (lat == null || lng == null) {
      seenNonce.current = nonce;
      return;
    }

    // Mark the nonce inside the frame so a cancelled setup (Strict Mode)
    // still retries. Land after useMapPathZoom's fitBounds in the same commit.
    // Maps animates panTo only for a short move and setZoom snaps, so card
    // clicks interpolate center and zoom together.
    const frame = window.requestAnimationFrame(() => {
      seenNonce.current = nonce;
      const currentZoom = map.getZoom() ?? Number.NaN;
      const targetZoom = propertyFocusZoom(currentZoom);
      const mapCenter = map.getCenter();
      const from = {
        lat: mapCenter?.lat() ?? lat,
        lng: mapCenter?.lng() ?? lng,
        zoom: Number.isFinite(currentZoom) ? currentZoom : targetZoom,
      };
      const mapRect = map.getDiv()?.getBoundingClientRect();
      const drawer = document
        .querySelector('[data-testid="property-detail-drawer"] aside')
        ?.getBoundingClientRect();
      const panByX = mapRect
        ? propertyFocusPanByX(
            { left: mapRect.left, right: mapRect.right },
            drawer ? { left: drawer.left, right: drawer.right } : null,
          )
        : 0;
      const destination = drawerShiftedCenter(
        map,
        { lat, lng },
        panByX,
        targetZoom,
      );
      const to = { ...destination, zoom: targetZoom };
      const startedAt = performance.now();

      const tick = (now: number) => {
        const progress = Math.min(
          1,
          (now - startedAt) / PROPERTY_FOCUS_ANIMATION_MS,
        );
        const sample = propertyFocusSample(progress, from, to);
        map.setCenter({ lat: sample.lat, lng: sample.lng });
        map.setZoom(sample.zoom);
        if (progress < 1) {
          animationRef.current = window.requestAnimationFrame(tick);
        }
      };
      animationRef.current = window.requestAnimationFrame(tick);
    });

    return () => {
      window.cancelAnimationFrame(frame);
      window.cancelAnimationFrame(animationRef.current);
    };
  }, [map, nonce, lat, lng]);

  return null;
}

function RouteBuilderMapOverlays({
  sessionId,
  visibleRoutes,
  focusedRouteId,
  dirtyRouteIds,
  markerPositions,
  onSelectRoute,
}: {
  sessionId: string | undefined;
  visibleRoutes: Array<{
    id: string;
    color: string;
    metrics: { drivingPath: string };
  }>;
  focusedRouteId: string | null;
  dirtyRouteIds: Set<string>;
  markerPositions: Array<{ lat: number; lng: number }>;
  onSelectRoute: (routeId: string) => void;
}) {
  const map = useMap();
  const pointerDepth = useRef(0);

  useEffect(() => {
    return () => {
      pointerDepth.current = 0;
      map?.setOptions({ draggableCursor: null });
    };
  }, [map]);

  const showRoutePointer = () => {
    pointerDepth.current += 1;
    map?.setOptions({ draggableCursor: "pointer" });
  };

  const hideRoutePointer = () => {
    pointerDepth.current = Math.max(0, pointerDepth.current - 1);
    if (pointerDepth.current === 0) {
      map?.setOptions({ draggableCursor: null });
    }
  };
  const routePaths = useMemo(
    () =>
      visibleRoutes
        .map((route) => route.metrics.drivingPath)
        .filter((path): path is string => Boolean(path)),
    [visibleRoutes],
  );

  // Refit for a new plan, route visibility, or recalculated paths. Property
  // edits and Unassigned changes update the markers without moving the map.
  const autoFitKey = JSON.stringify([
    sessionId,
    visibleRoutes.map((route) => [route.id, route.metrics.drivingPath]),
  ]);

  useMapPathZoom(
    routePaths,
    EMPTY_PATHS,
    undefined,
    markerPositions,
    autoFitKey,
  );

  return (
    <>
      {visibleRoutes.map((route) => {
        const path = route.metrics.drivingPath;
        if (!path) return null;
        const focused = route.id === focusedRouteId;
        const dimmed = focusedRouteId != null && !focused;
        const stale = dirtyRouteIds.has(route.id);
        return (
          <RoutePath
            key={`path-${route.id}`}
            path={path}
            color={resolveRouteColorPalette(route.color).hex}
            strokeOpacity={
              dimmed ? DIMMED_PATH_OPACITY : stale ? STALE_PATH_OPACITY : 1
            }
            strokeWeight={focused ? 6 : 5}
            zIndex={focused ? 20 : dimmed ? 5 : 10}
            pathClick={() => onSelectRoute(route.id)}
            onMouseOver={showRoutePointer}
            onMouseOut={hideRoutePointer}
          />
        );
      })}
    </>
  );
}

export default function RouteBuilderMap({
  showUnassignedMarkers,
  unassignedVisits,
}: {
  showUnassignedMarkers: boolean;
  unassignedVisits: Array<Pick<RouteStop, "propertyId" | "propertyVisitId">>;
}) {
  // Custom tooltips avoid the browser's delay for native title tooltips.
  const [activeTooltipId, setActiveTooltipId] = useState<string | null>(null);
  const hideTooltip = () => setActiveTooltipId(null);
  const {
    session,
    mapVisibleRoutes,
    selectedRouteId,
    setSelectedRouteId,
    selectedPropertyVisitId,
    selectPropertyFromMap,
    mapFocusNonce,
    mapFocusPropertyVisitId,
    getProperty,
    dirtyRouteIds,
  } = useRouteBuilder();

  const markers = useMemo(() => {
    if (!session) return [];
    return [
      ...mapVisibleRoutes.flatMap((route) =>
        route.stops.map((stop, index) => ({
          ...stop,
          routeId: route.id,
          index,
          color: route.color,
        })),
      ),
      ...unassignedMapStops({
        show: showUnassignedMarkers,
        // The same visits the Unassigned list shows, so the numbers match.
        visits: unassignedVisits,
        locationFor: (propertyId) => getProperty(propertyId)?.location,
      }),
    ].flatMap((stop) => {
      const property = getProperty(stop.propertyId);
      const position = toLatLngLiteral(property?.location);
      if (!property || !position) return [];
      return [
        {
          ...stop,
          position,
          name: property.name,
          address: formatAddress(property.address),
        },
      ];
    });
  }, [
    session,
    mapVisibleRoutes,
    getProperty,
    showUnassignedMarkers,
    unassignedVisits,
  ]);

  const selectedRoute =
    mapVisibleRoutes.find((route) => route.id === selectedRouteId) ?? null;
  // Only dim other routes while the selected route is on the map.
  const focusedRouteId = selectedRoute?.id ?? null;

  const endpointMarkers = useMemo(() => {
    if (!selectedRoute) return [];

    const start = toLatLngLiteral(selectedRoute.startLocation?.location);
    const end = toLatLngLiteral(selectedRoute.endLocation?.location);
    const samePoint =
      start != null &&
      end != null &&
      start.lat === end.lat &&
      start.lng === end.lng;

    if (samePoint) {
      return [
        {
          key: `${selectedRoute.id}-depot`,
          position: start,
          kind: "both" as const,
          title: `${selectedRoute.startLocation.name || "Location"} start and end`,
        },
      ];
    }

    return [
      start
        ? {
            key: `${selectedRoute.id}-start`,
            position: start,
            kind: "start" as const,
            title: `${selectedRoute.startLocation.name || "Location"} start`,
          }
        : null,
      end
        ? {
            key: `${selectedRoute.id}-end`,
            position: end,
            kind: "end" as const,
            title: `${selectedRoute.endLocation.name || "Location"} end`,
          }
        : null,
    ].filter(Boolean) as Array<{
      key: string;
      position: { lat: number; lng: number };
      kind: "start" | "end" | "both";
      title: string;
    }>;
  }, [selectedRoute]);

  // Include all displayed points when unassigned visits are present so fitting
  // them cannot hide assigned stops or depots before paths are calculated.
  const markerPositions = useMemo(
    () =>
      markers.some((marker) => marker.routeId === UNASSIGNED_ROUTE_ID)
        ? [...markers, ...endpointMarkers].map((marker) => marker.position)
        : [],
    [markers, endpointMarkers],
  );

  const center = endpointMarkers[0]?.position ??
    markers[0]?.position ?? { lat: 42.28, lng: -83.74 };

  const focusPosition =
    markers.find((marker) => marker.propertyVisitId === mapFocusPropertyVisitId)
      ?.position ?? null;

  const openProperty = (routeId: string, propertyVisitId: string) => {
    selectPropertyFromMap(routeId, propertyVisitId);
  };

  if (!API_KEY) {
    return (
      <div className="flex h-full items-center justify-center rounded-2xl border border-dashed border-slate-300 bg-slate-50 text-sm text-slate-500">
        Map API key not configured
      </div>
    );
  }

  return (
    <div className="relative h-full w-full overflow-hidden">
      <APIProvider apiKey={API_KEY}>
        <Map
          mapId={MAP_ID}
          style={{ width: "100%", height: "100%" }}
          defaultCenter={center}
          defaultZoom={12}
          gestureHandling="greedy"
          disableDefaultUI
          zoomControl
          zoomControlOptions={{ position: ControlPosition.RIGHT_BOTTOM }}
        >
          <RouteBuilderMapOverlays
            sessionId={session?.id}
            visibleRoutes={mapVisibleRoutes}
            focusedRouteId={focusedRouteId}
            dirtyRouteIds={dirtyRouteIds}
            markerPositions={markerPositions}
            onSelectRoute={setSelectedRouteId}
          />
          <FocusPropertyOnMap nonce={mapFocusNonce} position={focusPosition} />
          {endpointMarkers.map((marker) => (
            <AdvancedMarker
              key={marker.key}
              position={marker.position}
              zIndex={30}
            >
              <div
                className="flex h-8 min-w-8 items-center justify-center rounded-full border-2 border-sky-500 bg-white px-2 text-xs font-bold text-sky-700 shadow"
                title={marker.title}
              >
                <EndpointIcon kind={marker.kind} />
              </div>
            </AdvancedMarker>
          ))}
          {markers.map((marker) => {
            const isUnassigned = marker.routeId === UNASSIGNED_ROUTE_ID;
            const palette = resolveRouteColorPalette(marker.color);
            const selected = selectedPropertyVisitId === marker.propertyVisitId;
            const routeStale = dirtyRouteIds.has(marker.routeId);
            const dimmed =
              !isUnassigned &&
              focusedRouteId != null &&
              marker.routeId !== focusedRouteId;
            const tooltipId = `map-stop-tooltip-${marker.propertyVisitId}`;
            const tooltipVisible = activeTooltipId === tooltipId;
            const showTooltip = () => setActiveTooltipId(tooltipId);
            const title = [
              routeStale
                ? `${marker.name} (path outdated — re-optimize)`
                : isUnassigned
                  ? `${marker.name} (Unassigned)`
                  : marker.name,
              marker.address,
            ]
              .filter(Boolean)
              .join("\n");
            // Raise the active marker above other pins and depots for visibility.
            return (
              <AdvancedMarker
                key={marker.propertyVisitId}
                position={marker.position}
                zIndex={tooltipVisible ? 40 : selected ? 20 : dimmed ? 5 : 10}
                className="relative"
                onClick={() =>
                  openProperty(marker.routeId, marker.propertyVisitId)
                }
              >
                <button
                  type="button"
                  data-testid={`map-stop-pin-${marker.propertyVisitId}`}
                  className={`relative flex cursor-pointer items-center justify-center font-bold text-white ${
                    isUnassigned
                      ? `h-8 w-8 drop-shadow ${marker.index + 1 > 9 ? "text-[10px]" : "text-xs"}`
                      : `h-7 w-7 rounded-full text-xs shadow ${palette.bg}`
                  } ${
                    selected ? "ring-2 ring-offset-1 ring-sky-400" : ""
                  } ${dimmed || routeStale ? "opacity-50" : ""}`}
                  aria-label={`Open ${isUnassigned ? "unassigned property " : ""}${marker.name}`}
                  aria-describedby={tooltipId}
                  onMouseEnter={showTooltip}
                  onMouseLeave={hideTooltip}
                  onFocus={showTooltip}
                  onBlur={hideTooltip}
                  onKeyDown={(e) => {
                    if (e.key === "Escape") hideTooltip();
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    openProperty(marker.routeId, marker.propertyVisitId);
                  }}
                >
                  {isUnassigned ? (
                    <img
                      src={UnassignedTriangleIcon}
                      alt=""
                      className="absolute inset-0 h-full w-full"
                      aria-hidden
                    />
                  ) : null}
                  <span className="relative">{marker.index + 1}</span>
                </button>
                {/* Keep mounted to avoid recreating the Google Maps marker. */}
                <span
                  id={tooltipId}
                  role="tooltip"
                  hidden={!tooltipVisible}
                  className="pointer-events-none absolute left-1/2 top-full mt-2 w-max max-w-xs -translate-x-1/2 whitespace-pre-line break-words rounded bg-slate-800 px-3 py-2 text-xs font-normal text-white shadow-lg"
                >
                  {title}
                </span>
              </AdvancedMarker>
            );
          })}
        </Map>
      </APIProvider>
      <MapKey marks={mapKeyMarks(markers, endpointMarkers)} />
    </div>
  );
}
